// The P2P endpoints live in toolbox_db, mounted under db/ like every other model API.
export const API_BASE = '/api/db/p2p';

export const MAX_MEDIA_ITEMS = 10;
export const MAX_IMAGE_BYTES = 10 * 1024 * 1024;
export const IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/gif', 'image/webp'];

export interface PostMediaItem {
  id: number
  position: number
  kind: 'image' | 'video'
  source: 'youtube' | 'vidcast' | 'upload' | null
  videoUrl: string
  hasFile: boolean
  duration: string
  image: string
  videoFile: string
}

export type ComposerItem =
  | { id: string; kind: 'image'; file: File; previewUrl: string }
  | { id: string; kind: 'video'; source: 'upload'; file: File }
  | { id: string; kind: 'video'; source: 'youtube' | 'vidcast'; url: string }
  | { id: string; kind: 'existing'; item: PostMediaItem }

export type LinkSource = 'youtube' | 'vidcast';

export function extractYouTubeId(url: string): string | null {
  const match = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
  return match ? match[1] : null;
}

export function durationSeconds(d: string): number {
  const parts = d.split(':').map(Number);
  if (!d || parts.length > 3 || parts.some((n) => !Number.isFinite(n))) return 0;
  return parts.reduce((acc, n) => acc * 60 + n, 0);
}

export function hasDuration(d: string): boolean {
  return durationSeconds(d) > 0;
}

export function formatDuration(totalSeconds: number): string {
  if (!Number.isFinite(totalSeconds) || totalSeconds <= 0) return '0:00';
  const t = Math.round(totalSeconds);
  const h = Math.floor(t / 3600);
  const m = Math.floor((t % 3600) / 60);
  const sec = String(t % 60).padStart(2, '0');
  return h > 0 ? `${h}:${String(m).padStart(2, '0')}:${sec}` : `${m}:${sec}`;
}

export function readVideoDuration(file: File): Promise<string> {
  return new Promise((resolve) => {
    const src = URL.createObjectURL(file);
    const video = document.createElement('video');
    const done = (d: string) => {
      clearTimeout(timer);
      video.removeAttribute('src');
      URL.revokeObjectURL(src);
      resolve(d);
    };
    const timer = setTimeout(() => done('0:00'), 8000);
    video.preload = 'metadata';
    video.onloadedmetadata = () => done(formatDuration(video.duration));
    video.onerror = () => done('0:00');
    video.src = src;
  });
}

export function mapMediaItem(m: any, index: number): PostMediaItem {
  const hasFile = !!(m.image || m.video_file);
  const source = m.source || (m.kind === 'video' && hasFile ? 'upload' : null);
  return {
    id: Number(m.id ?? 0),
    position: Number(m.position ?? index),
    kind: m.kind === 'image' ? 'image' : 'video',
    source: source as PostMediaItem['source'],
    videoUrl: m.video_url || '',
    hasFile,
    duration: m.duration || '0:00',
    image: typeof m.image === 'string' ? m.image : '',
    videoFile: typeof m.video_file === 'string' ? m.video_file : '',
  };
}

export function mapMediaList(raw: unknown): PostMediaItem[] {
  return Array.isArray(raw) ? raw.map(mapMediaItem) : [];
}

// Uploaded items are resent by storage key, so a plain URL cannot be edited safely.
export function canEditMedia(media: PostMediaItem[]): boolean {
  return media.every((m) => !m.hasFile || !(m.kind === 'image' ? m.image : m.videoFile).startsWith('http'));
}

export function toComposerItems(media: PostMediaItem[]): ComposerItem[] {
  return media.map((item) => ({ id: `e${item.id}`, kind: 'existing', item }));
}

let nextId = 0;
const newId = () => `m${Date.now().toString(36)}${nextId++}`;

function validateFile(file: File): string | null {
  if (IMAGE_TYPES.includes(file.type)) {
    return file.size > MAX_IMAGE_BYTES ? `${file.name} is larger than 10 MB.` : null;
  }
  if (file.type.startsWith('video/')) return null;
  return `${file.name} is not a supported image or video file.`;
}

function validateLink(source: LinkSource, url: string): string | null {
  if (source === 'youtube') {
    return extractYouTubeId(url) ? null : 'Enter a valid YouTube link.';
  }
  return /^https:\/\/[a-z0-9.-]*vidcast\.io\//i.test(url) ? null : 'Enter a valid Vidcast link.';
}

export interface AddResult {
  items: ComposerItem[]
  error: string | null
}

const limitMessage = `A post can have at most ${MAX_MEDIA_ITEMS} items.`;

function makePreview(file: File): string {
  return typeof URL.createObjectURL === 'function' ? URL.createObjectURL(file) : '';
}

export function addFiles(items: ComposerItem[], files: File[]): AddResult {
  const next = [...items];
  const errors: string[] = [];
  let hitLimit = false;
  for (const file of files) {
    if (next.length >= MAX_MEDIA_ITEMS) { hitLimit = true; break; }
    const err = validateFile(file);
    if (err) { errors.push(err); continue; }
    next.push(
      IMAGE_TYPES.includes(file.type)
        ? { id: newId(), kind: 'image', file, previewUrl: makePreview(file) }
        : { id: newId(), kind: 'video', source: 'upload', file },
    );
  }
  if (hitLimit) errors.push(limitMessage);
  return { items: next, error: errors.length ? errors.join(' ') : null };
}

export function addLink(items: ComposerItem[], source: LinkSource, rawUrl: string): AddResult {
  if (items.length >= MAX_MEDIA_ITEMS) return { items, error: limitMessage };
  const url = rawUrl.trim();
  const err = validateLink(source, url);
  if (err) return { items, error: err };
  return { items: [...items, { id: newId(), kind: 'video', source, url }], error: null };
}

export function releaseItems(items: ComposerItem[]) {
  items.forEach((it) => {
    if (it.kind === 'image' && it.previewUrl && typeof URL.revokeObjectURL === 'function') {
      URL.revokeObjectURL(it.previewUrl);
    }
  });
}

export function removeItem(items: ComposerItem[], id: string): ComposerItem[] {
  return items.filter((it) => it.id !== id);
}

export function moveItem(items: ComposerItem[], id: string, dir: -1 | 1): ComposerItem[] {
  const from = items.findIndex((it) => it.id === id);
  const to = from + dir;
  if (from < 0 || to < 0 || to >= items.length) return items;
  const next = [...items];
  [next[from], next[to]] = [next[to], next[from]];
  return next;
}

type Uploaders = {
  image: (file: File) => Promise<string>
  video: (file: File) => Promise<string>
  duration?: (file: File) => Promise<string>
};

export async function buildMediaPayload(
  items: ComposerItem[],
  upload: Uploaders,
): Promise<Record<string, string>[]> {
  const media: Record<string, string>[] = [];
  for (const it of items) {
    if (it.kind === 'existing') {
      const m = it.item;
      if (m.kind === 'image') media.push({ kind: 'image', image_key: m.image });
      else if (m.hasFile) media.push({ kind: 'video', video_key: m.videoFile });
      else media.push({ kind: 'video', video_url: m.videoUrl, video_source: m.source || 'youtube', duration: m.duration });
    } else if (it.kind === 'image') {
      media.push({ kind: 'image', image_key: await upload.image(it.file) });
    } else if (it.source === 'upload') {
      const video_key = await upload.video(it.file);
      const duration = upload.duration ? await upload.duration(it.file) : '0:00';
      media.push({ kind: 'video', video_key, duration });
    } else {
      media.push({ kind: 'video', video_url: it.url, video_source: it.source, duration: '0:00' });
    }
  }
  return media;
}

export function mediaChanged(original: PostMediaItem[], items: ComposerItem[]): boolean {
  if (original.length !== items.length) return true;
  return items.some((it, i) => it.kind !== 'existing' || it.item.id !== original[i].id);
}

export async function buildMediaPatch(
  original: PostMediaItem[],
  items: ComposerItem[],
  upload: Uploaders,
): Promise<Record<string, string>[] | undefined> {
  if (!mediaChanged(original, items)) return undefined;
  return buildMediaPayload(items, upload);
}
